import { Button, SignInCard } from "@nymbl/nos2-rules";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getRouteApi, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useAuthOptions } from "@/hooks/use-auth-options";
import { usePopup } from "@/hooks/use-popup";
import { authClient, signIn } from "@/lib/auth-client";
import { DEV_ADMIN_PASSWORD, DEV_USER_PASSWORD } from "@/lib/dev-accounts";
import { POPUP_START_PATH } from "@/lib/embedding";
import { isEmbedded } from "@/lib/popup-sign-in";
import { devKeys, queryClient } from "@/lib/query";
import { loginPathFor } from "@/lib/return-path";
import { signInErrorMessage } from "@/lib/sign-in-errors";

const loginRoute = getRouteApi("/login");

const isDev = process.env.NODE_ENV !== "production";

const seedAccountsSchema = z.object({
  admin: z.object({ email: z.string().email() }),
  user: z.object({ email: z.string().email() }),
  /** The local account linked to a warehouse Person; null without its password. */
  writer: z.object({ email: z.string().email(), password: z.string().min(1) }).nullable(),
});

type DevAccount = { label: string; email: string; password: string };

// Emails come from a dev-only server endpoint so they always match the seeded rows.
function useDevAccounts() {
  return useQuery({
    queryKey: devKeys.seedAccounts,
    enabled: isDev,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async (): Promise<DevAccount[]> => {
      const res = await fetch("/api/dev/seed-accounts");
      if (!res.ok) return [];
      const parsed = seedAccountsSchema.parse(await res.json());
      return [
        ...(parsed.writer ? [{ label: "Writer", ...parsed.writer }] : []),
        { label: "Admin", email: parsed.admin.email, password: DEV_ADMIN_PASSWORD },
        { label: "User", email: parsed.user.email, password: DEV_USER_PASSWORD },
      ];
    },
  });
}

export function LoginForm() {
  const router = useRouter();
  const devAccounts = useDevAccounts().data ?? [];
  const options = useAuthOptions().data;
  const signUpOpen = options?.signUp === true;
  const showPassword = options?.password === true;
  const showMicrosoft = options?.microsoft === true;
  // Where the person was going when sign-in stopped them; checked by the route's schema.
  const { error: errorCode, redirect: returnPath } = loginRoute.useSearch();
  const errorMessage = signInErrorMessage(errorCode);
  const [problem, setProblem] = useState<PopupProblem | null>(null);

  function enterApp() {
    // A previous user's person must never show for this one.
    queryClient.clear();
    router.navigate({ href: returnPath ?? "/dashboard" });
  }

  const embedded = isEmbedded();
  const popupSignIn = usePopupSignIn(enterApp, setProblem);

  // Better Auth sends the window to Microsoft on success, so the button stays pending until
  // the page leaves. Only a failure to START comes back here. Inside Helm's frame the sign-in
  // runs in a popup instead (`usePopupSignIn`): Microsoft refuses to be framed.
  const microsoft = useMutation({
    mutationFn: async () => {
      const { error } = await signIn.social({
        provider: "microsoft",
        callbackURL: returnPath ?? "/dashboard",
        errorCallbackURL: loginPathFor(returnPath),
      });
      if (error) throw new Error(error.message ?? "Couldn't start Microsoft sign-in");
    },
    onError: (err) => toast.error(err.message),
  });

  async function signInWithPassword(values: { email: string; password: string }) {
    const { error } = await signIn.email(values);
    if (error) throw new Error(error.message ?? "Failed to sign in");
    // Inside Helm's frame, a browser that keeps cookies apart for framed sites (Safari,
    // Firefox) accepts the sign-in but never stores the session here; going on would bounce
    // straight back to this page. Say so, and offer the app in its own tab.
    if (embedded && !(await authClient.getSession()).data) {
      setProblem("session-blocked");
      return;
    }
    enterApp();
  }

  const showDevAccounts = showPassword && isDev && devAccounts.length > 0;
  const footer =
    problem || showDevAccounts || signUpOpen ? (
      <>
        {problem && <PopupProblem problem={problem} />}
        {showDevAccounts && (
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-muted-foreground">Sign in as:</span>
            {devAccounts.map((account) => (
              <Button
                key={account.label}
                variant="outline"
                size="sm"
                className="h-11"
                onClick={() =>
                  void signInWithPassword(account).catch((err: Error) => toast.error(err.message))
                }
              >
                {account.label}
              </Button>
            ))}
          </div>
        )}
        {signUpOpen && (
          <p className="text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Button asChild variant="link" className="h-auto p-0">
              <a href="/signup">Sign up</a>
            </Button>
          </p>
        )}
      </>
    ) : undefined;

  return (
    <SignInCard
      app="employee-dashboard"
      onMicrosoft={
        showMicrosoft ? () => (embedded ? popupSignIn.start() : microsoft.mutate()) : undefined
      }
      onPassword={showPassword ? signInWithPassword : undefined}
      error={popupSignIn.error ?? errorMessage}
      pending={microsoft.isPending || popupSignIn.waiting}
      pendingLabel={popupSignIn.waiting ? "Finish signing in in the popup…" : "Redirecting…"}
      footer={footer}
    />
  );
}

type PopupProblem = "blocked" | "session-blocked";

/**
 * Microsoft sign-in from inside Helm's frame. The popup opens on the app's own start page
 * (`POPUP_START_PATH`), which begins the sign-in from the popup's top-level page so Better
 * Auth's OAuth state cookie is first-party in every browser. When the popup reports back (or
 * just closes), the frame re-reads its own session; that read, not the message, decides.
 *
 * Signed in in the popup but still signed out here means the browser keeps cookies apart for
 * framed sites (Safari, Firefox): the frame reads another cookie jar, so trying again here
 * cannot help, and the way through is the app in its own tab.
 */
function usePopupSignIn(onSignedIn: () => void, onProblem: (problem: PopupProblem | null) => void) {
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function readSession(popupSaidSignedIn: boolean) {
    const session = await authClient.getSession();
    if (!mounted.current) return;
    if (session.data) onSignedIn();
    else if (popupSaidSignedIn) onProblem("session-blocked");
  }

  const popup = usePopup({
    onReport: (code) => {
      if (code === null) {
        void readSession(true);
        return;
      }
      setError(signInErrorMessage(code) ?? signInErrorMessage("unknown"));
    },
    // Closed without a report: re-read the session anyway, so a sign-in that did land is
    // never missed.
    onClosed: () => void readSession(false),
  });

  function start() {
    onProblem(null);
    setError(null);
    if (!popup.open(POPUP_START_PATH, "employee-dashboard-sign-in")) onProblem("blocked");
  }

  return { start, waiting: popup.waiting, error };
}

function PopupProblem({ problem }: { problem: PopupProblem }) {
  return (
    <div role="alert" className="space-y-1 text-center text-sm text-muted-foreground">
      <p>
        {problem === "blocked"
          ? "Your browser blocked the sign-in window. Allow pop-ups for this site, or:"
          : "You signed in, but your browser keeps the session out of this embedded view."}
      </p>
      <Button asChild variant="link" className="h-auto p-0">
        <a href="/dashboard" target="_blank" rel="noreferrer">
          Open the app in a new tab
        </a>
      </Button>
    </div>
  );
}
